import { Dispatch, SetStateAction, useContext, useState } from 'react';

import Auth from '@aws-amplify/auth';
import { useFormik } from 'formik';
import { Button, ErrorMessage, UiInput } from 'ui-kit';
import * as yup from 'yup';

import { YUP_PASSWORD_MATCH_MESSAGE, YUP_PASSWORD_MATCH_REGEX } from './validation';
import { AuthContext, AuthContextAction, AuthUser } from '../../context/AuthContext';

type Credentials = {
  email: string;
  password: string;
};

type Props = {
  user: AuthUser;
  credentials?: Credentials;
  onComplete: () => void;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
};

const NewPasswordRequired = ({ user, onComplete, setIsLoading, isLoading }: Props): JSX.Element => {
  const { dispatch } = useContext(AuthContext);
  const [error, setError] = useState<string>();

  const formik = useFormik({
    initialValues: {
      password: '',
      confirm_password: '',
    },
    validationSchema: yup.object().shape({
      password: yup
        .string()
        .required('Password is required')
        .matches(YUP_PASSWORD_MATCH_REGEX, YUP_PASSWORD_MATCH_MESSAGE),
      confirm_password: yup
        .string()
        .oneOf([yup.ref('password'), null], "Passwords don't match")
        .required('Confirm Password is required'),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(undefined);
      try {
        await Auth.completeNewPassword(user, values.password, {});

        // Successfully completed new password requirement
        setIsLoading(false);

        // Sign the user out so they can sign in with their new password
        await Auth.signOut();
        dispatch({
          type: AuthContextAction.SIGN_OUT,
        });

        // Return to sign-in form
        onComplete();
      } catch (err: any) {
        setIsLoading(false);
        setError(err.message || 'Failed to set new password. Please try again.');
      }
    },
  });

  return (
    <>
      <div className="mb-5 text-left font-heebo text-white">
        <h2 className="text-[24px] font-bold">Set New Password</h2>
        <p className="mt-2 text-[16px]">Please create a new password for your account</p>
      </div>

      <form onSubmit={formik.handleSubmit} className="flex flex-col gap-3">
        <div>
          <UiInput
            name="password"
            type="password"
            size="extra-large"
            variant="dark"
            label="New Password"
            placeholder="New Password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            data-testid="new-password-input"
          />
          <ErrorMessage className="mt-1 font-heebo">
            {formik.touched.password && formik.errors.password}
          </ErrorMessage>
        </div>

        <div>
          <UiInput
            name="confirm_password"
            type="password"
            size="extra-large"
            variant="dark"
            label="Confirm Password"
            placeholder="Confirm Password"
            value={formik.values.confirm_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirm_password && Boolean(formik.errors.confirm_password)}
            data-testid="confirm-password-input"
          />
          <ErrorMessage className="mt-1 font-heebo">
            {formik.touched.confirm_password && formik.errors.confirm_password}
          </ErrorMessage>

          {error && (
            <ErrorMessage className="mt-1 font-heebo" data-testid="error-message">
              {error}
            </ErrorMessage>
          )}
        </div>

        <Button
          label="Set Password"
          buttonType="submit"
          type="primary_light"
          disabled={!formik.isValid || isLoading}
          className="bg-gradient-to-r from-violet-500 to-violet-600"
          data-testid="set-password-button"
        >
          Set Password
        </Button>
      </form>
    </>
  );
};

export default NewPasswordRequired;
